import mudder from "mudder";
import { REL_ELEM, REL_PLAN, insertAllowed } from "./structure";

const DEBUG = true;

export const seqSpace = mudder.base62;
export const midSeq = seqSpace.mudder()[0];
// Ends of the (open) range of the seq values to use.
export const minSeq = seqSpace.num2sym[0];
export const maxSeq = seqSpace.num2sym[seqSpace.num2sym.length - 1];

function prepareReorderWithDebug(parent, insertItems, insertPos) {
  const relation = parent.childRel;
  const children = parent.getChildren();

  function debug(header, items) {
    console.debug(header);
    for (const item of items) {
      const name = (item.name || "").slice(0, 30);
      const parentID = item[relation] && item[relation].key;
      const seq = item[relation] && item[relation].seq;
      console.debug(` - ${item._id} "${name}" -> ${parentID}, ${seq}`);
    }
  }

  if (DEBUG) {
    console.group(`prepareReorder (${relation}):`);
    debug("Before (item -> parent, seq):", insertItems);

    const seqs = children.map((item) => item[relation].seq);
    const parentName = (parent.name || "").slice(0, 30);
    console.debug(`New parent: ${parent._id} "${parentName}"`);
    console.debug(`Sibling seqs: ${seqs}`);
    console.debug(
      `Insert between: ${seqs[insertPos - 1]}, ${seqs[insertPos]}`,
      `(position: ${insertPos})`,
    );
    performance.mark("start");
  }

  const updated = prepareReorder(parent, insertItems, insertPos);

  if (DEBUG) {
    performance.mark("stop");
    if (updated.length) debug("After (item -> parent, seq):", updated);
    else console.debug("Nothing to do.");

    const m = performance.measure("prepareReorder", "start", "stop");
    console.debug(`Time elapsed: ${m.duration.toFixed(2)} ms`);
    console.groupEnd();
  }

  return updated;
}

function prepareReorder(parent, insertItems, insertPos) {
  if (!insertAllowed(parent, insertItems)) {
    return [];
  }
  const relation = parent.childRel;
  let children = parent.getChildren();
  const updatedSeqs = calcNewSeqs(insertItems, relation, children, insertPos);

  // Not only insertItems may need to be updated, but some neighbors, too.
  const items = {};
  for (const item of insertItems) {
    items[item._id] = item;
  }
  for (const item of children) {
    items[item._id] = item;
  }

  const updated = [];
  for (const itemID of Object.keys(updatedSeqs)) {
    const newParentPtr = { key: parent._id, seq: updatedSeqs[itemID] };
    updated.push(items[itemID].clone({ [relation]: newParentPtr }));
  }
  return updated;
}

/**
 * Figure out new ordinal strings to carry out insert operation.
 *
 * @param insertObjs - array of objects, in the order they will be inserted
 * @param relation - name of the relation for which the order is calculated
 * @param siblings - children of the new parent, we insert between them
 * @param insertPos - index in sibling array before which we insert
 * @returns a map of ID->seq for all objects that need to be updated
 *
 * The only assumptions are that insertObjs and siblings have "_id" property
 * and that siblings have [relation] property with "seq" attribute present.
 *
 * Note: In general case not only the "input" objects may need to be updated -
 * some neighbors may need updated seqs. However, in case of putting objects
 * at the beginning/end, this should never happen.
 *
 * Note that this function does not check whether inserting objects make sense
 * (e.g. that it won't create a cycle). That should be checked elsewhere.
 */
function calcNewSeqs(insertObjs, relation, siblings, insertPos) {
  if (!Array.isArray(siblings)) {
    throw new Error("calcNewSeqs: siblings is not an array");
  }
  if (insertObjs.length === 0) {
    return {};
  }
  if (insertPos === undefined) {
    insertPos = siblings.length; // Appending as a reasonable default.
  }

  const insertCount = insertObjs.length;

  // Check for the special case when nothing is actually changing.
  let nextObjIdx = 0;
  for (let i = 0; i < siblings.length; ++i) {
    if (siblings[i]._id === insertObjs[nextObjIdx]._id) {
      ++nextObjIdx;
      if (nextObjIdx == insertCount) {
        // All insertObjs are packed consecutively inside siblings array.
        if (insertPos > i - insertCount && insertPos <= i + 1) {
          // The insertPos is within current insertObjs positions.
          return {};
        }
        // There will be some change, nothing more to find in this loop.
        break;
      }
    } else if (nextObjIdx > 0) {
      // Chain of matching objects was broken, there will be an actual change.
      break;
    }
  }

  const seqsByObjID = {};

  // "Edge" case: insert at the beginning/end of the given container.
  if (insertPos === 0 || insertPos === siblings.length) {
    // Don't distribute seqs evenly - leave more space near the ends
    // to accomodate future appends/prepends.
    // When inserting into an empty container, assign seqs just before
    // the middle of the available space. Otherwise assign seqs just
    // before the first / just after the last existing child.
    const seqs = siblings.map((obj) => obj[relation].seq);
    const startSeq = insertPos === 0 ? minSeq : seqs[seqs.length - 1];
    seqs.push(maxSeq);
    const endSeq = siblings.length ? seqs[insertPos] : midSeq;

    const divisions = insertCount + seqSpace.num2sym.length;
    const newSeqs = seqSpace.mudder(startSeq, endSeq, divisions);
    newSeqs.splice(insertPos ? insertCount : 0, seqSpace.num2sym.length);

    for (let i = 0; i < insertCount; i++) {
      seqsByObjID[insertObjs[i]._id] = newSeqs[i];
    }
    return seqsByObjID;
  }

  // Insert between other objects.
  const insertIds = new Set(insertObjs.map((obj) => obj._id));
  // Decrese the insertPos to be correct after filtering out insertObjs.
  const objsBeforeInsertPos = insertPos - 1;
  for (let i = 0; i < objsBeforeInsertPos; ++i) {
    if (insertIds.has(siblings[i]._id)) {
      --insertPos;
    }
  }
  // Filter insertObjs out of siblings list.
  // It spares us some tricky corner cases.
  siblings = siblings.filter((obj) => !insertIds.has(obj._id));

  // Figure out the ordering.
  const seqs = siblings.map((obj) => obj[relation].seq);
  const prevSeq = seqs[insertPos - 1];
  const nextSeq = seqs[insertPos];

  // Simple case - no duplicated seqs present.
  if (prevSeq != nextSeq) {
    const insertSeqs = seqSpace.mudder(prevSeq, nextSeq, insertCount);
    for (let i = 0; i < insertCount; ++i) {
      seqsByObjID[insertObjs[i]._id] = insertSeqs[i];
    }
    return seqsByObjID;
  }

  // Handle duplicated seq.
  // Note: minSeq and maxSeq are never duplicated - they are sentinel values
  // and should never be assigned to actual objects.
  const dupedSeq = prevSeq;
  const firstDupIdx = seqs.indexOf(dupedSeq);
  const lastDupIdx = seqs.lastIndexOf(dupedSeq);
  const newSeqsCount = lastDupIdx - firstDupIdx + 1 + insertCount;
  const newSeqs = seqSpace.mudder(
    seqs[firstDupIdx - 1],
    seqs[lastDupIdx + 1],
    newSeqsCount,
  );
  for (let i = 0; i < insertCount; ++i) {
    seqsByObjID[insertObjs[i]._id] = newSeqs[insertPos - firstDupIdx + i];
  }

  // Update seqs in objects before the ones inserted.
  for (let i = firstDupIdx; i < insertPos; ++i) {
    seqsByObjID[insertObjs[i]._id] = newSeqs[i - firstDupIdx];
  }

  // Update seqs in objects after the ones inserted.
  for (let i = insertPos; i <= lastDupIdx; ++i) {
    seqsByObjID[insertObjs[i]._id] = newSeqs[i - firstDupIdx + 1];
  }

  return seqsByObjID;
}

function docCmpForRelation(relation) {
  return (a, b) => {
    if (!a[relation]) {
      console.error(`Malformed object ${a._id}: no ${relation}`)
      return false
    }
    if (!b[relation]) {
      console.error(`Malformed object ${b._id}: no ${relation}`)
      return false
    }
    if (a[relation].seq && typeof a[relation].seq !== "string") {
      console.error(`${relation}.seq of ${a._id} is not a string:`, a[relation].seq);
      return false;
    }
    if (b[relation].seq && typeof b[relation].seq !== "string") {
      console.error(`${relation}.seq of ${b._id} is not a string:`, b[relation].seq);
      return true;
    }
    if (a[relation].seq === b[relation].seq) {
      return a._id < b._id;
    }
    return a[relation].seq < b[relation].seq;
  };
}

// Based on underscore's sortedIndex.
function findInsertIndex(parentList, relation, item) {
  const cmp = docCmpForRelation(relation);
  let low = 0;
  let high = parentList.length;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (cmp(parentList[mid], item)) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
}

//////////////////
/// PUBLIC API ///
//////////////////

export function sortIntoParentList(parentList, relation, item) {
  const idx = findInsertIndex(parentList, relation, item);
  parentList.splice(idx, 0, item);
}

export function removeFromParentList(parentList, relation, item) {
  const idx = parentList.findIndex((chd) => chd._id === item._id);
  if (!parentList[idx] || parentList[idx]._id !== item._id) {
    console.error(`Item ${item._id} not in ${relation} parent list`);
    return;
  }
  parentList.splice(idx, 1);
}

export function insertItems(parent, items, insertPos) {
  const updated = prepareReorderWithDebug(parent, items, insertPos);

  if (parent.childRel === REL_ELEM) {
    // unplan items if their new parent has the same REL_PLAN parent
    // TODO: handle case when elements of X are planned for a period
    // and then X gets planned for this period, too. (elements should get unplanned)
    for (const item of updated) {
      const itemPlanned = item[REL_PLAN] && item[REL_PLAN].key;
      const parentPlanned = parent[REL_PLAN] && parent[REL_PLAN].key;
      if (itemPlanned === parentPlanned) {
        delete item[REL_PLAN];
      }
    }
  }
  return updated;
}

export function insertItemsBefore(sibling, parent, items) {
  const insertPos = parent.getChildren().findIndex((el) => el._id === sibling._id);
  return insertItems(parent, items, insertPos);
}

// should this accept relation instead of parent?
export function insertItemsAfter(sibling, parent, items) {
  const siblingPos = parent.getChildren().findIndex((el) => el._id === sibling._id);
  return insertItems(parent, items, siblingPos + 1);
}

export function appendItems(parent, items) {
  const insertPos = parent.getChildren().length;
  return insertItems(parent, items, insertPos);
}

export function appendItem(parent, item) {
  const updated = appendItems(parent, [item]);
  if (updated.length !== 1) {
    throw `Got ${updated.length} elements instead of 1: ${updated}`;
  }
  return updated[0];
}

export function swapWithPrevious(item, parent) {
  const position = item.getPosition() - 1;
  return insertItems(parent, [item], position);
}

export function swapWithNext(item, parent) {
  const position = item.getPosition() + 2;
  return insertItems(parent, [item], position);
}
